import { weightDayOf, type Measurement, type MeasurementType } from "./health";

// TES-1481 (TES-1458 §5, Ö-805) — one rule for how her measurements are
// drawn, whoever reads them. Emelie 2026-10-20:
// "Gemensamma grafgrupper och skalor används för patient och behörig personal."
//
// The app's "Grafer och trender" and the doctor's patient page both draw
// from these functions; they may differ in size and labelling, never in
// which values share a chart, where a point sits, or what a line is joined
// from.
//
// TES-1510 — 09B, "Gemensam grafstruktur — precisering 2026-10-19":
// "X-axeln är alltid en proportionerlig tidsaxel."
// "Blodtryck/vilopuls: samma grupp och tidsaxel, två deldiagram"
//
//   · Groups: one per measure, in the app's order, except that blood
//     pressure and resting pulse are ONE group of two charts on one time
//     axis. Blood pressure is one chart with systolic and diastolic on a
//     shared scale — they are one reading; pulse has its own scale.
//   · Points: the values that count now (`current`), oldest first. A
//     replaced value is history, not a point on the curve.
//   · Axis: every type on a proportional time axis. A group's axis holds
//     every occasion (measurement session) of its charts, so blood pressure
//     and pulse taken together stand on the same spot.
//   · Drawn: the weight from 1 occasion (TES-1356), every other group from 2.
//   · Scale: per chart, from its lowest to its highest value; a flat series
//     sits in the middle.

export type MeasurementChartGroupKey =
  | "weight"
  | "waist"
  | "blood_pressure_pulse"
  | "height"
  | "hip";

/** The groups, in the order "Grafer och trender" shows them. */
export const MEASUREMENT_CHART_ORDER: readonly MeasurementChartGroupKey[] = [
  "weight",
  "waist",
  "blood_pressure_pulse",
  "height",
  "hip",
];

/** The charts of each group, in the order they are drawn. */
export const MEASUREMENT_CHART_GROUP_TYPES: Record<
  MeasurementChartGroupKey,
  readonly MeasurementType[]
> = {
  weight: ["weight"],
  waist: ["waist"],
  blood_pressure_pulse: ["blood_pressure", "pulse"],
  height: ["height"],
  hip: ["hip"],
};

/** How many occasions a group needs before it is drawn as a chart. */
export function chartMinOccasions(key: MeasurementChartGroupKey): number {
  return key === "weight" ? 1 : 2;
}

export interface MeasurementChartPoint {
  /** The measurement's id. */
  id: string;
  /** The occasion (measurement session): the point's place on the axis. */
  key: string;
  measuredAt: string;
  value: number;
}

export interface MeasurementChartLine {
  key: "value" | "systolic" | "diastolic";
  /** Drawn as the secondary line (diastolic), without a fill. */
  muted: boolean;
  /** Oldest first. Every line of a chart has the same measurements. */
  points: MeasurementChartPoint[];
}

export interface ChartValueDomain {
  min: number;
  max: number;
}

export interface MeasurementChart {
  type: MeasurementType;
  lines: MeasurementChartLine[];
  /** Shared by every line of the chart. */
  domain: ChartValueDomain;
}

/** One occasion on a group's time axis. */
export interface MeasurementChartOccasion {
  key: string;
  measuredAt: string;
}

export interface MeasurementChartGroup {
  key: MeasurementChartGroupKey;
  /** Every occasion of the group's charts, oldest first: the shared axis. */
  axis: MeasurementChartOccasion[];
  /** The group's charts that have a point, in the group's order. */
  charts: MeasurementChart[];
  /** Enough occasions to be drawn (chartMinOccasions). */
  drawn: boolean;
}

/** The lowest and highest value; the scale every line of a chart shares. */
export function chartValueDomain(values: number[]): ChartValueDomain {
  return { min: Math.min(...values), max: Math.max(...values) };
}

/**
 * Height of a value on the chart: 0 at the bottom (the lowest value), 1 at
 * the top (the highest). A flat series has no range and sits at 0.5.
 */
export function chartHeight(domain: ChartValueDomain, value: number): number {
  const range = domain.max - domain.min;
  return range === 0 ? 0.5 : (value - domain.min) / range;
}

const byMeasuredAt = (a: { measuredAt: string }, b: { measuredAt: string }) =>
  a.measuredAt.localeCompare(b.measuredAt);

const LINES: Record<
  "blood_pressure" | "other",
  Array<[MeasurementChartLine["key"], boolean]>
> = {
  blood_pressure: [
    ["systolic", false],
    ["diastolic", true],
  ],
  other: [["value", false]],
};

/**
 * The chart of one measurement type, or null when it has no point.
 * `measurements` are in the server's order (newest first, as both the
 * patient's and the doctor's list return them); readings taken at the same
 * moment keep the order they were recorded in. A blood pressure is a point
 * only with both pressures, so its two lines hold the same readings.
 */
function chartOf(
  measurements: Measurement[],
  type: MeasurementType,
): MeasurementChart | null {
  const keys = LINES[type === "blood_pressure" ? "blood_pressure" : "other"];
  const live = measurements
    .filter(
      (m) => m.type === type && m.current && keys.every(([k]) => m[k] !== null),
    )
    .reverse()
    .sort(byMeasuredAt);
  if (live.length === 0) return null;
  const lines = keys.map(
    ([key, muted]): MeasurementChartLine => ({
      key,
      muted,
      points: live.map((m) => ({
        id: m.id,
        key: m.sessionId,
        measuredAt: m.measuredAt,
        value: Number(m[key]),
      })),
    }),
  );
  return {
    type,
    lines,
    domain: chartValueDomain(
      lines.flatMap((l) => l.points.map((p) => p.value)),
    ),
  };
}

/** One group of her charts, whether or not it has enough to be drawn. */
export function measurementChartGroup(
  measurements: Measurement[],
  key: MeasurementChartGroupKey,
): MeasurementChartGroup {
  const charts = MEASUREMENT_CHART_GROUP_TYPES[key].flatMap((type) => {
    const chart = chartOf(measurements, type);
    return chart ? [chart] : [];
  });
  const occasions = new Map<string, string>();
  for (const chart of charts) {
    for (const p of chart.lines[0]!.points) {
      if (!occasions.has(p.key)) occasions.set(p.key, p.measuredAt);
    }
  }
  const axis = [...occasions.entries()]
    .map(([occasion, measuredAt]) => ({ key: occasion, measuredAt }))
    .sort(byMeasuredAt);
  return { key, axis, charts, drawn: axis.length >= chartMinOccasions(key) };
}

/** Every group she has enough of to draw, in the app's order. */
export function measurementChartGroups(
  measurements: Measurement[],
): MeasurementChartGroup[] {
  return MEASUREMENT_CHART_ORDER.map((key) =>
    measurementChartGroup(measurements, key),
  ).filter((g) => g.drawn);
}

// TES-1356 — the weight chart's time axis. Emelie 2026-10-14:
// "X-axeln ska vara en kontinuerlig och proportionerlig tidsaxel baserad på mätdatum (measured_at)."

const DAY_MS = 86_400_000;

/** Whole days since 1970-01-01 for a calendar day "YYYY-MM-DD". */
export function dayNumber(day: string): number {
  const [y, m, d] = day.split("-").map(Number);
  return Math.round(Date.UTC(y!, m! - 1, d!) / DAY_MS);
}

/** 0 = Sunday … 6 = Saturday, for a calendar day "YYYY-MM-DD". */
export function weekdayOf(day: string): number {
  return new Date(dayNumber(day) * DAY_MS).getUTCDay();
}

/** The calendar day a time-axis point is placed on (the tenant's zone). */
export const chartDayOf = weightDayOf;

export interface TimeScaleOptions {
  width: number;
  padLeft: number;
  padRight: number;
  // Horizontal distance between readings taken on the same day. A day has
  // one position on the axis; without this, two readings that day would sit
  // on top of each other and one of them could never be tapped.
  sameDayGap: number;
}

/**
 * The x position of each reading, in input order. `days` are the readings'
 * calendar days, oldest first.
 *
 * x is proportional to the number of days since the first reading, so 14
 * days between two readings is twice the distance of 7. Only readings get a
 * position — a day without one creates nothing. A single day (one reading,
 * or several the same day) sits in the middle.
 *
 * Readings on the same day are spread `sameDayGap` apart around that day's
 * position, and the group is pulled inwards when it would leave the plot.
 */
export function timeScaleX(days: string[], opts: TimeScaleOptions): number[] {
  if (days.length === 0) return [];
  const left = opts.padLeft;
  const right = opts.width - opts.padRight;
  const nums = days.map(dayNumber);
  const first = Math.min(...nums);
  const span = Math.max(...nums) - first;
  const base = nums.map((n) =>
    span === 0
      ? (left + right) / 2
      : left + ((n - first) / span) * (right - left),
  );

  const groups = new Map<number, number[]>();
  nums.forEach((n, i) => groups.set(n, [...(groups.get(n) ?? []), i]));

  const xs = [...base];
  for (const indices of groups.values()) {
    if (indices.length < 2) continue;
    const groupWidth = (indices.length - 1) * opts.sameDayGap;
    const centred = base[indices[0]!]! - groupWidth / 2;
    const start = Math.max(left, Math.min(centred, right - groupWidth));
    indices.forEach((i, k) => {
      xs[i] = start + k * opts.sameDayGap;
    });
  }
  return xs;
}

/**
 * TES-1510 — the x of each occasion on a group's axis, by occasion key.
 * Every chart of the group places its points with it, so blood pressure and
 * pulse taken together line up, in the app and on the doctor's page alike.
 */
export function chartAxisX(
  axis: MeasurementChartOccasion[],
  opts: TimeScaleOptions,
): Map<string, number> {
  const xs = timeScaleX(
    axis.map((a) => chartDayOf(a.measuredAt)),
    opts,
  );
  return new Map(axis.map((a, i) => [a.key, xs[i]!]));
}
